import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sparkles, Loader2, Mail, Lock, User, ArrowRight, Check, ArrowLeft, Crown, Star, Users } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('trial'); // 'trial', 'basic', or 'pro'
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  const { register, login } = useAuth();
  const navigate = useNavigate();

  // Check for referral code in URL or localStorage
  useEffect(() => {
    const refCode = searchParams.get('ref') || localStorage.getItem('kylo_ref_code');
    if (refCode) {
      setAffiliateCode(refCode.toUpperCase());
    }
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate('/creator-dashboard');
      } else {
        // Register the user first
        await register(formData.name, formData.email, formData.password);

        // If user selected a paid plan, send straight to checkout/pricing.
        if (selectedPlan === 'basic' || selectedPlan === 'pro') {
          toast.success('Account created! Taking you to checkout…');
          navigate('/pricing');
          return;
        }
        toast.success('Account created! Your free trial has started.');
        navigate('/creator-dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail });
      setResetSent(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setResetSent(true);
      toast.success('If an account exists, reset instructions have been sent.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setResetSent(false);
  };

  const selectPlan = (plan) => {
    setSelectedPlan(plan);
    setIsLogin(false); // Ensure we're in register mode
  };

  const benefits = [
    '3-day free trial — no card required',
    'Hooks, scripts &amp; filming ideas for TikTok Shop',
    'Pitch-ready UGC scripts for brand collabs',
    'Save and export ideas anytime',
  ];

  const getPlanLabel = () => {
    if (selectedPlan === 'basic') return 'Basic (£9.99/month)';
    if (selectedPlan === 'pro') return 'Pro (£15.99/month)';
    return 'Free Trial';
  };

  const getButtonText = () => {
    if (isLogin) return 'Sign In';
    if (selectedPlan === 'basic') return 'Create Account & Subscribe';
    if (selectedPlan === 'pro') return 'Create Account & Subscribe';
    return 'Start Free Trial';
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO title="Sign In" description="Sign in or create your Poettik account." noindex />
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Logo />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="ghost" size="sm">
                  Pricing
                </Button>
              </Link>
              <Link to="/affiliate-interest">
                <Button variant="ghost" size="sm">
                  <Users className="w-4 h-4 mr-2" />
                  Become an Affiliate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left side - Benefits */}
        <div className="hidden lg:flex lg:w-1/2 relative z-10 p-12 flex-col justify-center">
          <h1 className="font-display text-4xl font-bold mb-6">
            For TikTok Shop creators <span className="gradient-text">&amp; UGC pros</span>
          </h1>

          <p className="text-lg text-muted-foreground mb-10 max-w-md">
            Whether you&apos;re selling on TikTok Shop or pitching brands as a UGC creator, Poettik writes the hooks, the angles and a tight script ready to film.
          </p>

          <div className="space-y-4 mb-10">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-foreground">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Plan selection */}
          {!isLogin && (
            <div className="p-6 rounded-2xl glass-card">
              <p className="font-semibold text-foreground mb-4">Choose your plan:</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => selectPlan('trial')}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    selectedPlan === 'trial' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === 'trial' ? 'border-primary bg-primary' : 'border-muted-foreground'
                  }`}>
                    {selectedPlan === 'trial' && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <div className="text-left">
                    <p className="font-medium">Free Trial</p>
                    <p className="text-xs text-muted-foreground">3 days free, no card required</p>
                  </div>
                </button>

                <button
                  onClick={() => selectPlan('basic')}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    selectedPlan === 'basic' 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === 'basic' ? 'border-primary bg-primary' : 'border-muted-foreground'
                  }`}>
                    {selectedPlan === 'basic' && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  <Star className="w-4 h-4 text-primary" />
                  <div className="text-left flex-1">
                    <p className="font-medium">Basic</p>
                    <p className="text-xs text-muted-foreground">£9.99/month</p>
                  </div>
                </button>

                <button
                  onClick={() => selectPlan('pro')}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    selectedPlan === 'pro' 
                      ? 'border-accent bg-accent/10' 
                      : 'border-border hover:border-accent/50'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === 'pro' ? 'border-accent bg-accent' : 'border-muted-foreground'
                  }`}>
                    {selectedPlan === 'pro' && <Check className="w-3 h-3 text-accent-foreground" />}
                  </div>
                  <Crown className="w-4 h-4 text-accent" />
                  <div className="text-left flex-1">
                    <p className="font-medium">Pro</p>
                    <p className="text-xs text-muted-foreground">£15.99/month</p>
                  </div>
                  <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full">Best Value</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right side - Form */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative z-10">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden mb-8 text-center">
              <Link to="/" className="inline-flex items-center gap-2">
                <Logo size="lg" />
              </Link>
            </div>

            <Card variant="glass" className="p-2">
              {/* Forgot Password View */}
              {showForgotPassword ? (
                <>
                  <CardHeader className="text-center pb-2">
                    <CardTitle className="font-display text-2xl">
                      {resetSent ? 'Check Your Email' : 'Reset Password'}
                    </CardTitle>
                    <CardDescription>
                      {resetSent 
                        ? 'We\'ve sent password reset instructions to your email'
                        : 'Enter your email and we\'ll send you reset instructions'
                      }
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    {resetSent ? (
                      <div className="text-center py-6">
                        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                          <Mail className="w-8 h-8 text-primary" />
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                          If an account exists for <strong>{forgotEmail}</strong>, you'll receive an email with instructions to reset your password.
                        </p>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={resetForgotPassword}
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Back to Sign In
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="forgot-email">Email Address</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              id="forgot-email"
                              type="email"
                              placeholder="you@example.com"
                              className="pl-10"
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                              required
                              data-testid="forgot-email-input"
                            />
                          </div>
                        </div>

                        <Button
                          type="submit"
                          variant="premium"
                          size="lg"
                          className="w-full mt-6"
                          disabled={isLoading}
                          data-testid="send-reset-btn"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              Send Reset Link
                              <ArrowRight className="w-5 h-5 ml-2" />
                            </>
                          )}
                        </Button>
                      </form>
                    )}
                  </CardContent>

                  {!resetSent && (
                    <CardFooter className="pt-0">
                      <Button
                        variant="ghost"
                        className="w-full"
                        onClick={resetForgotPassword}
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Sign In
                      </Button>
                    </CardFooter>
                  )}
                </>
              ) : (
                /* Regular Login/Register View */
                <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-display text-2xl">
                  {isLogin ? 'Welcome Back' : 'Create Your Account'}
                </CardTitle>
                <CardDescription>
                  {isLogin 
                    ? 'Sign in to continue creating viral content' 
                    : selectedPlan === 'trial'
                      ? 'Start your 3-day free trial - no credit card required'
                      : `Sign up for ${getPlanLabel()}`
                  }
                </CardDescription>
              </CardHeader>

              <CardContent>
                {/* Mobile plan selection */}
                {!isLogin && (
                  <div className="lg:hidden mb-6 p-4 rounded-xl bg-muted/30">
                    <p className="text-sm font-medium mb-3">Select your plan:</p>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => selectPlan('trial')}
                        className={`p-2 rounded-lg text-center text-xs transition-all ${
                          selectedPlan === 'trial' 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        Free Trial
                      </button>
                      <button
                        onClick={() => selectPlan('basic')}
                        className={`p-2 rounded-lg text-center text-xs transition-all ${
                          selectedPlan === 'basic' 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        £9.99/mo
                      </button>
                      <button
                        onClick={() => selectPlan('pro')}
                        className={`p-2 rounded-lg text-center text-xs transition-all ${
                          selectedPlan === 'pro' 
                            ? 'bg-accent text-accent-foreground' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        £15.99/mo
                      </button>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="name"
                          placeholder="John Doe"
                          className="pl-10"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required={!isLogin}
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        data-testid="auth-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        required
                        minLength={6}
                        data-testid="auth-password-input"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      {!isLogin && (
                        <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
                      )}
                      {isLogin && (
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-xs text-primary hover:underline"
                          data-testid="forgot-password-link"
                        >
                          Forgot your password?
                        </button>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant={selectedPlan === 'pro' ? 'premium' : 'gradient'}
                    size="lg"
                    className="w-full mt-6"
                    disabled={isLoading}
                    data-testid="auth-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {isLogin ? 'Signing in...' : 'Creating account...'}
                      </>
                    ) : (
                      <>
                        {selectedPlan === 'pro' && <Crown className="w-5 h-5 mr-2" />}
                        {selectedPlan === 'basic' && <Star className="w-5 h-5 mr-2" />}
                        {getButtonText()}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  {/* Payment note for paid plans */}
                  {!isLogin && (selectedPlan === 'basic' || selectedPlan === 'pro') && (
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      You'll be redirected to Stripe for secure payment
                    </p>
                  )}
                </form>
              </CardContent>

              <CardFooter className="flex flex-col gap-4 pt-0">
                <div className="relative w-full">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-card px-2 text-muted-foreground">
                      {isLogin ? 'New to Poettik?' : 'Already have an account?'}
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setIsLogin(!isLogin)}
                  data-testid="toggle-auth-mode-btn"
                >
                  {isLogin ? 'Create an account' : 'Sign in instead'}
                </Button>
              </CardFooter>
                </>
              )}
            </Card>

            <p className="text-center text-xs text-muted-foreground mt-6">
              By creating an account, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/50 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
          </div>
          <div className="flex items-center gap-4">
            <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Pricing
            </Link>
            <Link to="/affiliate-interest" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Become an Affiliate
            </Link>
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Poettik. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthPage;
